// Single source of truth for human-readable subscription-tier names and
// upgrade-target relationships. Imported by both the API billing service
// (apps/api/src/services/billingService.ts) and the frontend Plan card
// (apps/web/src/components/PlanCard.tsx) so the two surfaces never drift.
//
// Keep the canonical tier names in sync with:
//   - apps/api/src/config/tier-capabilities.ts (TIER_CAPABILITIES, TIER_ORDER)
//   - apps/api/src/db/migrations/0117_tighten_subscription_tier_check.sql
//     (CHECK constraint vocabulary)
//   - apps/web/src/lib/tier.ts (TIER_* constants)

export const PLAN_DISPLAY_NAMES: Record<string, string> = {
  INDIVIDUAL:       'Individual',
  STARTER:          'Starter',
  GROWTH:           'Growth',
  ENTERPRISE:       'Enterprise',
  PLATFORM_PARTNER: 'Platform Partner',
};

/**
 * Human-readable plan name for a tier value. Falls back to the raw tier
 * string when unknown so we surface a non-empty label (better than
 * rendering `undefined`); callers that want to detect unknown tiers
 * should compare to `PLAN_DISPLAY_NAMES[tier] ?? null` directly.
 */
export function getPlanDisplayName(tier: string | null | undefined): string {
  if (!tier) return 'Unknown plan';
  return PLAN_DISPLAY_NAMES[tier] ?? tier;
}

/**
 * Next-step upgrade target for self-serve tiers. Returns null when the
 * tier has no self-serve upgrade path (GROWTH already has the full agent
 * set; ENTERPRISE/PLATFORM_PARTNER are contract tiers).
 *
 *   INDIVIDUAL → STARTER
 *   STARTER    → GROWTH
 *   else       → null
 */
export function getUpgradeTarget(tier: string | null | undefined): string | null {
  if (tier === 'INDIVIDUAL') return 'STARTER';
  if (tier === 'STARTER')    return 'GROWTH';
  return null;
}

// ── Per-tier feature lists (issue #786) ────────────────────────────────────
// Surface-level "what's in my plan" copy for the Plan card popover. Each
// tier maps to two ordered lists: things included, and things explicitly
// not included (so the user understands what an upgrade would unlock).
//
// Source of truth for the underlying gating logic: tier-capabilities.ts
// in apps/api/src/config/. The strings here are human-readable summaries,
// not the raw agent names.

export interface TierFeatures {
  included: string[];
  excluded: string[];
}

const FEATURES_INDIVIDUAL: TierFeatures = {
  included: [
    '8 SDLC agents — PM, Architecture, UX, Sprint Planning, Dev, ERD, Test Plan, Deployment Plan',
    'GitHub + Google Drive integration',
    'Account profile and notification preferences',
    'Single-seat solo workspace',
  ],
  excluded: [
    'Team members and invitations',
    'Code Review, QA, Deployment, Change Order agents',
    'Security Review, Performance Analysis, Retrospective, Risk Management agents',
    'Tool integrations (Jira, Azure DevOps, Confluence, Notion)',
    'Phase Gate Templates, Escalation Automation, Phase Configuration Defaults',
    'MS Teams notification channel',
  ],
};

const FEATURES_STARTER: TierFeatures = {
  included: [
    'All 12 core agents — adds Code Review, QA, Deployment, Change Order on top of the solo set',
    'Team members and invitations (no seat cap)',
    'Tool integrations (Jira, Azure DevOps, Confluence, Notion)',
    'All notification channels (Email, Slack, MS Teams, WhatsApp)',
    'Platform Access Log (security audit)',
  ],
  excluded: [
    'Security Review, Performance Analysis, Retrospective, Risk Management agents',
    'Phase Gate Templates',
    'Escalation Automation',
    'Phase Configuration Defaults',
  ],
};

// Higher-tier feature lists are explicit (not "Everything in <lower tier>")
// so each popover is self-contained. A user reading the GROWTH features
// shouldn't have to switch to the STARTER popover to know what they're
// getting (clarity feedback on #786 implementation).

const FEATURES_GROWTH: TierFeatures = {
  included: [
    'All 16 agents — 12 core (PM, Architecture, UX, Sprint Planning, Dev, Code Review, QA, Deployment, Change Order, ERD, Test Plan, Deployment Plan) + 4 extended (Security Review, Performance Analysis, Retrospective, Risk Management)',
    'Team members and invitations (no seat cap)',
    'Tool integrations (Jira, Azure DevOps, Confluence, Notion)',
    'All notification channels (Email, Slack, MS Teams, WhatsApp)',
    'Platform Access Log (security audit)',
    'Phase Gate Templates',
    'Escalation Automation',
    'Phase Configuration Defaults',
  ],
  excluded: [
    'Custom contract pricing (Enterprise)',
    'White-label partner agreement and multi-client management (Platform Partner)',
  ],
};

const FEATURES_ENTERPRISE: TierFeatures = {
  included: [
    'All 16 agents (full agent set, including the 4 extended agents)',
    'Team members and invitations (no seat cap)',
    'Tool integrations (Jira, Azure DevOps, Confluence, Notion)',
    'All notification channels (Email, Slack, MS Teams, WhatsApp)',
    'Platform Access Log (security audit)',
    'Phase Gate Templates, Escalation Automation, Phase Configuration Defaults',
    'Custom contract pricing',
    'Dedicated account manager',
    'Volume discounts on agent usage',
  ],
  excluded: [
    'White-label partner agreement (see Platform Partner)',
    'Multi-client tenant management',
  ],
};

const FEATURES_PLATFORM_PARTNER: TierFeatures = {
  included: [
    'All 16 agents (full agent set, including the 4 extended agents)',
    'Team members and invitations (no seat cap)',
    'Tool integrations (Jira, Azure DevOps, Confluence, Notion)',
    'All notification channels (Email, Slack, MS Teams, WhatsApp)',
    'Platform Access Log (security audit)',
    'Phase Gate Templates, Escalation Automation, Phase Configuration Defaults',
    'White-label partner agreement',
    'Multi-client tenant management — create + manage client tenants',
    'Billing portal access (seats, usage, invoice history)',
    'Partner-level configuration defaults inherited by client tenants',
  ],
  excluded: [],
};

/**
 * Returns the human-readable feature lists for the given tier. Unknown /
 * retired aliases (BLUEPRINT_CORE/_FULL etc.) return empty lists so the
 * caller renders nothing rather than a confusing partial display.
 */
export function getTierFeatures(tier: string | null | undefined): TierFeatures {
  switch (tier) {
    case 'INDIVIDUAL':       return FEATURES_INDIVIDUAL;
    case 'STARTER':          return FEATURES_STARTER;
    case 'GROWTH':           return FEATURES_GROWTH;
    case 'ENTERPRISE':       return FEATURES_ENTERPRISE;
    case 'PLATFORM_PARTNER': return FEATURES_PLATFORM_PARTNER;
    default:                 return { included: [], excluded: [] };
  }
}
